import {
  type AdRemoteOperationsArea,
  useOptionalAdRemoteOperationsArea,
  useRemoteOperationsAreaOperatorRole,
} from '@backoffice-frontend/ad-remote-operations-area'
import {
  adFleetMapAlertTypes,
  fleetControlAlertStatus,
} from '@backoffice-frontend/alerts-common'
import { useErrorHandlingPollingQuery } from '@backoffice-frontend/data-fetching'
import { createVehiclesProvider } from '@backoffice-frontend/fleet-maps-common'
import {
  useGlobalRegion,
  useSelectedFleets,
} from '@backoffice-frontend/service-area-picker'
import { adVehicleFetchIntervalInMs } from '@backoffice-frontend/vehicle-common'
import {
  type AdVehicleFragment,
  useAdFleetMapVehiclesQuery,
} from './AdFleetMapVehicles.hook'
import { hasMapData } from './utils'

const areaRendersAdMapPanel: Record<AdRemoteOperationsArea, boolean> = {
  'ad-fleet-map': true,
  'remote-assistance-operator': true,
  'rider-support': false,
}

const useVehiclesQuery = () => {
  const [region] = useGlobalRegion()
  const selectedFleetIds = useSelectedFleets()
  const role = useRemoteOperationsAreaOperatorRole()
  const remoteOperationsArea = useOptionalAdRemoteOperationsArea()

  const withMapData = remoteOperationsArea
    ? areaRendersAdMapPanel[remoteOperationsArea]
    : true

  const { data, loading } = useErrorHandlingPollingQuery(
    useAdFleetMapVehiclesQuery,
    region
      ? {
          variables: {
            regionId: region.id,
            taskNotificationTypes: adFleetMapAlertTypes,
            taskNotificationStatus: fleetControlAlertStatus,
            withAdAlerts: true,
            withHandlingOperator: true,
            withMapData,
            role,
          },
          fetchPolicy: 'no-cache',
          pollInterval: adVehicleFetchIntervalInMs,
          // using `initialFetchPolicy` to avoid uninteded caching when polling
          // see https://github.com/apollographql/apollo-client/blob/72c625d96f172e25563f5bba6e45531abac1deb3/src/core/ObservableQuery.ts#L750-L754
          initialFetchPolicy: 'no-cache',
        }
      : { skip: true },
  )

  const allVehicles: AdVehicleFragment[] = data?.fleetMapVehicles.vehicles ?? []

  const vehicles = allVehicles.filter(
    vehicle => vehicle.fleet && selectedFleetIds.includes(vehicle.fleet.id),
  )

  return {
    vehicles,
    loading,
  }
}

export const { Provider: AdVehiclesProvider, useVehicles: useAdVehicles } =
  createVehiclesProvider<AdVehicleFragment>({
    useVehiclesQuery,
  })

export const useAdMapVehicles = () => {
  const { vehicles, selectedVehicle, ...rest } = useAdVehicles()

  return {
    ...rest,
    vehicles: vehicles.filter(hasMapData),
    selectedVehicle:
      selectedVehicle && hasMapData(selectedVehicle)
        ? selectedVehicle
        : undefined,
  }
}
